import React, { useState, useEffect } from 'react';
import { 
  FileCheck, 
  Plus, 
  Trash2, 
  Edit3, 
  Save, 
  Clock, 
  X, 
  CheckCircle2, 
  FileText 
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import './FormsUnified.css';

export default function AdmissionsRequirementsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({ 
    title: '', 
    subtitle: '', 
    description: '', 
    requirements: [] 
  });
  const [pendingFields, setPendingFields] = useState({ 
    title: false, 
    subtitle: false, 
    description: false 
  });
  const [pendingRequirements, setPendingRequirements] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  
  // Modal state
  const [isAddingReq, setIsAddingReq] = useState(false);
  const [editingReqIndex, setEditingReqIndex] = useState(null);
  const [modalReqData, setModalReqData] = useState(null);
  const [newDocText, setNewDocText] = useState('');
  const [newEligText, setNewEligText] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadData = async () => {
    setLoading(true);
    try {
      let section = null;

      // 1. Fetch admissions sections via admin endpoint
      try {
        const response = await fetch(`${apiBase}/admissions/cms/sections`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const result = await response.json();
          if (result.ok && Array.isArray(result.data)) {
            section = result.data.find(s => s.section_type === 'admissions-requirements');
          }
        }
      } catch (err) {
        console.warn('[AdmissionsRequirementsForm] Admin fetch warning:', err.message);
      }

      // 2. Fallback to public query if needed
      if (!section) {
        try {
          const pubRes = await fetch(`${apiBase}/admissions/cms/sections?published=true`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pubRes.ok) {
            const pubResult = await pubRes.json();
            if (pubResult.ok && Array.isArray(pubResult.data)) {
              section = pubResult.data.find(s => s.section_type === 'admissions-requirements');
            }
          }
        } catch (pubErr) {
          console.warn('[AdmissionsRequirementsForm] Public fetch warning:', pubErr.message);
        }
      }

      if (!section) {
        throw new Error('Admissions Requirements section not found');
      }

      setSectionId(section.id);

      let content = section.content_published || section.content || {};
      if (typeof content === 'string') {
        try { content = JSON.parse(content); } catch (e) { content = {}; }
      }
      const en = content?.en || content || {};
      const rawReqs = Array.isArray(en.requirements) ? en.requirements : [];
      const cleanReqs = rawReqs
        .filter(r => r && typeof r === 'object')
        .map(r => ({
          level: r.level || r.title || '',
          description: r.description || '',
          documents: Array.isArray(r.documents) ? r.documents : [],
          eligibility: Array.isArray(r.eligibility || r.criteria) ? (r.eligibility || r.criteria) : []
        }));

      const initialData = {
        title: en.title || '',
        subtitle: en.subtitle || en.kicker || '',
        description: en.description || '',
        requirements: cleanReqs
      };

      const pMap = { title: false, subtitle: false, description: false };
      const rMap = {};

      // 3. Check for pending proposals in the workflow
      try {
        const pRes = await fetch(`${apiBase}/cms/section/${section.id}/with-pending`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (pRes.ok) {
          const pResult = await pRes.json();
          if (pResult.success && Array.isArray(pResult.data?.pending_changes)) {
            pResult.data.pending_changes.forEach(c => {
              if (c.status === 'pending' && (c.locale === 'en' || !c.locale)) {
                let val = c.new_value;
                if (typeof val === 'string') {
                  try { val = JSON.parse(val); } catch (e) {}
                }

                if (['title', 'subtitle', 'description'].includes(c.field_id)) {
                  pMap[c.field_id] = true;
                  if (typeof val === 'string') {
                    initialData[c.field_id] = val;
                  }
                } else if (c.field_id && c.field_id.includes('requirements.')) {
                  const m = c.field_id.match(/requirements\.(\d+)\.(add|update|delete)/);
                  if (m) {
                    const reqIdx = parseInt(m[1], 10);
                    const op = m[2];
                    rMap[`req_${reqIdx}`] = true;
                    if (op === 'delete') {
                      rMap[`req_${reqIdx}_delete`] = true;
                    } else if (op === 'add') {
                      rMap[`req_${reqIdx}_add`] = true;
                      if (val && !initialData.requirements[reqIdx]) {
                        initialData.requirements.push(val);
                      }
                    } else if (op === 'update') {
                      if (val && initialData.requirements[reqIdx]) {
                        initialData.requirements[reqIdx] = { ...initialData.requirements[reqIdx], ...val };
                      }
                    }
                  }
                }
              }
            });
          }
        }
      } catch (pErr) {
        console.warn('[AdmissionsRequirementsForm] Pending changes check warning:', pErr.message);
      }

      setFormData(initialData);
      setOriginalData(JSON.parse(JSON.stringify(initialData)));
      setPendingFields(pMap);
      setPendingRequirements(rMap);
    } catch (err) {
      console.error('[AdmissionsRequirementsForm] Load error:', err.message);
      toast.error('Failed to load admissions requirements: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitField = async (name, label) => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (formData[name] === originalData[name]) { 
      toast.warning('No changes to submit'); 
      return; 
    }

    setSubmitting(p => ({ ...p, [name]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: name,
          field_name: label || `Admissions Requirements ${name.replace(/_/g, ' ')}`,
          old_value: originalData[name] || '',
          new_value: formData[name] || '',
          locale: 'en'
        })
      });
      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Submitted ${label || name} for Principal approval`);
        setPendingFields(p => ({ ...p, [name]: true }));
        setOriginalData(p => ({ ...p, [name]: formData[name] }));
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for ${label || name} is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [name]: false }));
    }
  };

  // Open Add Requirement Modal
  const handleOpenAddRequirement = () => {
    if (formData.requirements.length >= 10) {
      toast.warning('Maximum 10 requirement levels allowed');
      return;
    }
    const newReq = { 
      level: '', 
      description: '', 
      documents: [], 
      eligibility: [] 
    };
    setIsAddingReq(true);
    setEditingReqIndex(formData.requirements.length);
    setModalReqData(newReq);
    setNewDocText('');
    setNewEligText('');
  };

  // Open Edit Requirement Modal
  const handleOpenEditRequirement = (idx) => {
    const target = formData.requirements[idx];
    if (!target) return;
    setIsAddingReq(false);
    setEditingReqIndex(idx);
    setModalReqData({
      level: target.level || '',
      description: target.description || '',
      documents: Array.isArray(target.documents) ? [...target.documents] : [],
      eligibility: Array.isArray(target.eligibility) ? [...target.eligibility] : []
    });
    setNewDocText('');
    setNewEligText('');
  };

  const handleCloseModal = () => {
    setIsAddingReq(false);
    setEditingReqIndex(null);
    setModalReqData(null);
    setNewDocText('');
    setNewEligText('');
  };

  const handleModalReqChange = (field, value) => {
    if (!modalReqData) return;
    setModalReqData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddDoc = () => {
    if (!newDocText.trim() || !modalReqData) return;
    setModalReqData(prev => ({
      ...prev,
      documents: [...(prev.documents || []), newDocText.trim()]
    }));
    setNewDocText('');
  };

  const handleRemoveDoc = (docIdx) => {
    if (!modalReqData) return;
    setModalReqData(prev => ({
      ...prev,
      documents: (prev.documents || []).filter((_, i) => i !== docIdx)
    }));
  };

  const handleAddElig = () => {
    if (!newEligText.trim() || !modalReqData) return;
    setModalReqData(prev => ({
      ...prev,
      eligibility: [...(prev.eligibility || []), newEligText.trim()]
    }));
    setNewEligText('');
  };

  const handleRemoveElig = (eligIdx) => {
    if (!modalReqData) return;
    setModalReqData(prev => ({
      ...prev,
      eligibility: (prev.eligibility || []).filter((_, i) => i !== eligIdx)
    }));
  };

  // Submit Requirement from Modal (Add or Update)
  const handleSaveRequirementModal = async () => {
    if (!sectionId) { 
      toast.error('Section ID not available'); 
      return; 
    }
    if (!modalReqData || !modalReqData.level?.trim()) { 
      toast.warning('Level name is required'); 
      return; 
    }

    const idx = editingReqIndex;
    const isNew = isAddingReq;
    const op = isNew ? 'add' : 'update';
    const submitKey = isNew ? 'add_req' : `req_${idx}`;

    setSubmitting(p => ({ ...p, [submitKey]: true }));

    const fieldId = `requirements.${idx}.${op}`;
    const fieldName = `${isNew ? 'Add' : 'Update'} Admissions Requirement: ${modalReqData.level.trim()}`;
    const oldValue = isNew ? null : (originalData.requirements[idx] || {});

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: fieldName,
          old_value: oldValue,
          new_value: modalReqData,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || res.ok) {
        toast.success(`Requirement for "${modalReqData.level}" submitted for Principal approval`);
        setPendingRequirements(p => ({
          ...p,
          [`req_${idx}`]: true,
          [`req_${idx}_add`]: isNew
        }));

        if (isNew) {
          setFormData(prev => ({
            ...prev,
            requirements: [...prev.requirements, { ...modalReqData }]
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            requirements: prev.requirements.map((r, i) => (i === idx ? { ...modalReqData } : r))
          }));
        }

        handleCloseModal();
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for this requirement is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error submitting requirement: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [submitKey]: false }));
    }
  };

  // Propose Deletion of Requirement by Level (Submitted to Principal!)
  const handleDeleteRequirement = async (idx) => {
    const targetReq = formData.requirements[idx];
    if (!targetReq) return;

    if (!window.confirm(`Are you sure you want to propose deleting "${targetReq.level || `Level ${idx + 1}`}"? This will require Principal approval.`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID not available');
      return;
    }

    const key = `del_req_${idx}`;
    setSubmitting(p => ({ ...p, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `requirements.${idx}.delete`,
          field_name: `Delete Admissions Requirement: ${targetReq.level || `Level ${idx + 1}`}`,
          old_value: targetReq,
          new_value: null,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || res.ok) {
        toast.success(`Deletion of "${targetReq.level || `Level ${idx + 1}`}" submitted for Principal approval`);
        setPendingRequirements(p => ({
          ...p,
          [`req_${idx}`]: true,
          [`req_${idx}_delete`]: true
        }));
      } else {
        if (res.status === 409) {
          toast.warning(`A pending proposal for this requirement is already awaiting review`);
        } else {
          toast.error(result.error || result.message || 'Delete proposal failed');
        }
      }
    } catch (err) {
      toast.error('Error submitting delete proposal: ' + err.message);
    } finally {
      setSubmitting(p => ({ ...p, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading admissions requirements...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <FileCheck size={22} />
        </div>
        <div>
          <h2 className="unified-title">Admissions Requirements</h2>
          <p className="unified-subtitle">
            Manage grade level eligibility, required documentation, and submission checklists
          </p>
        </div>
      </div>

      {/* Section Subtitle Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Tag / Subtitle</label>
            <p className="unified-field-desc">Kicker label above the main heading (e.g. Requirements)</p>
          </div>
          {pendingFields.subtitle && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.subtitle}
            onChange={(e) => handleFieldChange('subtitle', e.target.value)}
            disabled={submitting.subtitle || pendingFields.subtitle}
            placeholder="e.g. Requirements"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('subtitle', 'Requirements Subtitle')}
            disabled={submitting.subtitle || pendingFields.subtitle || formData.subtitle === originalData.subtitle}
          >
            <Save size={14} />
            {submitting.subtitle ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Title Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Main Section Title</label>
            <p className="unified-field-desc">Primary heading introducing documentation and eligibility requirements</p>
          </div>
          {pendingFields.title && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Documents & Eligibility by Level"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title', 'Requirements Title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
          >
            <Save size={14} />
            {submitting.title ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Section Description Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Section Description</label>
            <p className="unified-field-desc">Introductory text helping families prepare their applications with confidence</p>
          </div>
          {pendingFields.description && (
            <span className="unified-pending-pill">
              <Clock size={11} /> Pending Review
            </span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="e.g. Clear requirements for each admission level to help families prepare their applications..."
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('description', 'Requirements Description')}
            disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
          >
            <Save size={14} />
            {submitting.description ? 'Submitting...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Requirements by Level Card */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Requirements by Level ({formData.requirements.length})</label>
            <p className="unified-field-desc">Checklists and criteria configured for Preschool, Primary, and Secondary</p>
          </div>
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={handleOpenAddRequirement}
          >
            <Plus size={14} /> Add Requirement Level
          </button>
        </div>

        {formData.requirements.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#64748b' }}>
            <FileCheck size={32} style={{ margin: '0 auto 10px', opacity: 0.5 }} />
            <p style={{ margin: 0, fontWeight: 600 }}>No requirement levels configured</p>
            <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "Add Requirement Level" above to add grade specifications</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
            {formData.requirements.map((req, idx) => {
              const key = `req_${idx}`;
              const isPending = pendingRequirements[key];
              const isPendingDelete = pendingRequirements[`req_${idx}_delete`];
              const isPendingAdd = pendingRequirements[`req_${idx}_add`];
              const docsCount = Array.isArray(req.documents) ? req.documents.length : 0;
              const eligCount = Array.isArray(req.eligibility) ? req.eligibility.length : 0;

              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    padding: '16px 20px',
                    background: isPendingDelete ? '#fff1f2' : '#f8fafc',
                    borderRadius: '12px',
                    border: `1px solid ${isPendingDelete ? '#fecdd3' : '#e2e8f0'}`,
                    transition: 'all 0.2s ease',
                    opacity: isPendingDelete ? 0.8 : 1
                  }}
                >
                  <div
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '10px',
                      background: isPendingDelete ? '#fee2e2' : '#eff6ff',
                      color: isPendingDelete ? '#ef4444' : '#2563eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <FileText size={20} />
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <h4 style={{ margin: 0, fontSize: '15.5px', fontWeight: 800, color: '#0f172a' }}>
                        {req.level || `Requirement Level ${idx + 1}`}
                      </h4>
                      {isPendingDelete ? (
                        <span 
                          className="unified-pending-pill" 
                          style={{ padding: '2px 8px', fontSize: '10px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}
                        >
                          <Trash2 size={10} /> Pending Deletion
                        </span>
                      ) : isPendingAdd ? (
                        <span 
                          className="unified-pending-pill" 
                          style={{ padding: '2px 8px', fontSize: '10px', background: '#dcfce7', color: '#15803d', border: '1px solid #86efac' }}
                        >
                          <Plus size={10} /> Proposed New
                        </span>
                      ) : isPending ? (
                        <span className="unified-pending-pill" style={{ padding: '2px 8px', fontSize: '10px' }}>
                          <Clock size={10} /> Pending Review
                        </span>
                      ) : null}
                    </div>

                    <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                      {req.description || 'No description entered.'}
                    </p>

                    {/* Document Badges */}
                    {docsCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>
                          Required Documents ({docsCount}):
                        </span>
                        {req.documents.slice(0, 4).map((doc, di) => (
                          <span
                            key={di}
                            style={{
                              fontSize: '11px',
                              background: '#e0e7ff',
                              color: '#3730a3',
                              padding: '2px 8px',
                              borderRadius: '6px',
                              fontWeight: 500
                            }}
                          >
                            {doc}
                          </span>
                        ))}
                        {docsCount > 4 && (
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>+{docsCount - 4} more</span>
                        )}
                      </div>
                    )}

                    {/* Eligibility Badges */}
                    {eligCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>
                          Eligibility Criteria ({eligCount}):
                        </span>
                        {req.eligibility.slice(0, 3).map((elig, ei) => (
                          <span
                            key={ei}
                            style={{
                              fontSize: '11px',
                              background: '#ecfdf5',
                              color: '#065f46',
                              padding: '2px 8px',
                              borderRadius: '6px',
                              fontWeight: 500
                            }}
                          >
                            {elig}
                          </span>
                        ))}
                        {eligCount > 3 && (
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>+{eligCount - 3} more</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <button
                      type="button"
                      className="unified-secondary-btn"
                      onClick={() => handleOpenEditRequirement(idx)}
                      disabled={isPendingDelete || isPending}
                    >
                      <Edit3 size={14} /> Edit
                    </button>
                    {!isPendingDelete && (
                      <button
                        type="button"
                        onClick={() => handleDeleteRequirement(idx)}
                        disabled={submitting[`del_req_${idx}`] || isPendingDelete}
                        style={{
                          padding: '8px 12px',
                          color: '#ef4444',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '8px',
                          cursor: (submitting[`del_req_${idx}`] || isPendingDelete) ? 'not-allowed' : 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontWeight: 600,
                          fontSize: '12px'
                        }}
                        title="Propose deletion of this requirement level"
                      >
                        <Trash2 size={14} />
                        <span>{submitting[`del_req_${idx}`] ? 'Submitting...' : 'Delete'}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit Requirement Modal */}
      {(isAddingReq || editingReqIndex !== null) && modalReqData && (
        <div
          className="unified-modal-overlay"
          onClick={handleCloseModal}
        >
          <div className="unified-modal" style={{ maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>
                  {isAddingReq ? 'Add Requirement Level' : `Edit Requirements: ${modalReqData.level || `Level ${(editingReqIndex || 0) + 1}`}`}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Configure required paperwork, eligibility criteria, and instructions
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Level Title */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Level Name *
                </label>
                <input
                  type="text"
                  className="unified-input"
                  value={modalReqData.level || ''}
                  onChange={(e) => handleModalReqChange('level', e.target.value)}
                  placeholder="e.g. Preschool & Kindergarten, Primary, or Secondary"
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                  Description / General Overview
                </label>
                <textarea
                  className="unified-textarea"
                  rows={3}
                  value={modalReqData.description || ''}
                  onChange={(e) => handleModalReqChange('description', e.target.value)}
                  placeholder="Overview of expectations for this age group..."
                />
              </div>

              {/* Required Documents List */}
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
                  Required Documents Checklist
                </label>

                {/* Add Doc Row */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newDocText}
                    onChange={(e) => setNewDocText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDoc())}
                    placeholder="e.g. Birth certificate, Family card (KK), Previous report cards..."
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={handleAddDoc}
                    style={{ padding: '8px 16px', fontSize: '12.5px' }}
                  >
                    <Plus size={14} /> Add Document
                  </button>
                </div>

                {/* Doc Chips */}
                {modalReqData.documents && modalReqData.documents.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {modalReqData.documents.map((doc, di) => (
                      <span
                        key={di}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '5px 10px',
                          background: '#ffffff',
                          border: '1px solid #cbd5e1',
                          borderRadius: '8px',
                          fontSize: '12.5px',
                          color: '#1e293b'
                        }}
                      >
                        <FileText size={12} color="#2563eb" />
                        {doc}
                        <button
                          type="button"
                          onClick={() => handleRemoveDoc(di)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            padding: 0
                          }}
                          aria-label={`Remove ${doc}`}
                        >
                          <X size={13} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: '12.5px', color: '#94a3b8' }}>
                    No documents listed yet. Add items using the field above.
                  </p>
                )}
              </div>

              {/* Eligibility Criteria List */}
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#1e293b', marginBottom: '8px' }}>
                  Eligibility Criteria
                </label>

                {/* Add Eligibility Row */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    className="unified-input"
                    value={newEligText}
                    onChange={(e) => setNewEligText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddElig())}
                    placeholder="e.g. Minimum age of 3 years by July, Successful assessment interview..."
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    className="unified-submit-button"
                    onClick={handleAddElig}
                    style={{ padding: '8px 16px', fontSize: '12.5px' }}
                  >
                    <Plus size={14} /> Add Criterion
                  </button>
                </div>

                {/* Elig Chips */}
                {modalReqData.eligibility && modalReqData.eligibility.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {modalReqData.eligibility.map((elig, ei) => (
                      <span
                        key={ei}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '5px 10px',
                          background: '#ffffff',
                          border: '1px solid #cbd5e1',
                          borderRadius: '8px',
                          fontSize: '12.5px',
                          color: '#1e293b'
                        }}
                      >
                        <CheckCircle2 size={12} color="#16a34a" />
                        {elig}
                        <button
                          type="button"
                          onClick={() => handleRemoveElig(ei)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            padding: 0
                          }}
                          aria-label={`Remove ${elig}`}
                        >
                          <X size={13} />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: '12.5px', color: '#94a3b8' }}>
                    No eligibility criteria added yet.
                  </p>
                )}
              </div>
            </div>

            <div className="unified-modal-footer">
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                disabled={submitting[isAddingReq ? 'add_req' : `req_${editingReqIndex}`] || !modalReqData.level?.trim()}
                onClick={handleSaveRequirementModal}
              >
                <Save size={14} />
                {submitting[isAddingReq ? 'add_req' : `req_${editingReqIndex}`]
                  ? 'Submitting...'
                  : (isAddingReq ? 'Submit New Requirement' : 'Save & Submit Requirement')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
